import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Grid,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Chip,
  alpha,
  useTheme,
  Paper,
  Avatar,
  TextField,
  Divider,
  useMediaQuery,
  Tab,
  Tabs
} from "@mui/material";
import {
  Sync as SyncIcon,
  People as PeopleIcon,
  AdminPanelSettings as AdminIcon,
  Person as UserIcon,
  Email as EmailIcon,
  CalendarToday as DateIcon,
  BusinessOutlined as BusinessIcon,
  VerifiedOutlined as VerifiedIcon,
  BlockOutlined as BlockIcon,
  VisibilityOutlined as ViewIcon,
  SecurityOutlined as SecurityIcon
} from "@mui/icons-material";
import { motion } from "framer-motion";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { toast } from "react-toastify";
import api from "../../services/api";
import ModernLoader from "../../components/common/ModernLoader";
import moment from "moment";

const AdminUsers = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { allPartners, loading: partnerLoading } = useSelector(s => s.partner);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/users/${userId}`, { role: newRole });
      toast.success(`Access level adjusted to ${newRole.toUpperCase()}`);
      fetchUsers(false);
    } catch (error) {
      toast.error("Process failure in role synchronization");
    }
  };

  const fetchUsers = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await api.get("/users?limit=500");
      const data = response.data.users || response.data || [];
      setUsers(data.map(u => ({
        ...u,
        id: u._id,
        name: u.name || "Unknown Identity",
        email: u.email || "N/A",
        role: u.role || 'user',
        createdAt: u.createdAt
      })));
    } catch (error) {
      console.error("Fetch users error:", error);
      toast.error("Identity database unreachable");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    const { getAllPartners } = require("../../store/slices/partnerSlice");
    dispatch(getAllPartners({}));
  }, [fetchUsers, dispatch]);

  const handlePartnerAction = (partner, action) => {
    // Placeholder for partner actions like view, suspend, reactivate
    toast.info(`Executing context-aware action: ${action.toUpperCase()}`);
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      await api.post('/auth/sync-users');
      toast.success('Identity synchronization complete');
      fetchUsers(false);
    } catch (error) {
      toast.error('Sync process failed');
    } finally {
      setSyncing(false);
    }
  };

  const columns = useMemo(() => [
    {
      accessorKey: "name",
      header: "Identity Profile",
      Header: ({ column }) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <PeopleIcon sx={{ fontSize: 18, color: "primary.main" }} />
          <Typography variant="caption" sx={{ fontWeight: 900, textTransform: "uppercase", letterSpacing: "1px" }}>{column.columnDef.header}</Typography>
        </Stack>
      ),
      Cell: ({ row, cell }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{
              width: 42, height: 42,
              bgcolor: row.original.role === 'admin' ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.info.main, 0.1),
              color: row.original.role === 'admin' ? "error.main" : "info.main",
              fontWeight: 900,
              fontSize: "1rem"
            }}
          >
            {cell.getValue()[0]}
          </Avatar>
          <Box>
            <Typography variant="body1" fontWeight={850} sx={{ color: theme.palette.text.primary }}>{cell.getValue()}</Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>ID: {row.original.id?.slice(-8).toUpperCase()}</Typography>
          </Box>
        </Box>
      ),
    },
    {
      accessorKey: "email",
      header: "Comm Channel",
      Header: ({ column }) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <EmailIcon sx={{ fontSize: 18, color: "secondary.main" }} />
          <Typography variant="caption" sx={{ fontWeight: 900, textTransform: "uppercase", letterSpacing: "1px" }}>{column.columnDef.header}</Typography>
        </Stack>
      ),
      Cell: ({ cell }) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" fontWeight={700} sx={{ color: "text.primary" }}>{cell.getValue()}</Typography>
        </Stack>
      ),
    },
    {
      accessorKey: "role",
      header: "Access Privilege",
      Header: ({ column }) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <AdminIcon sx={{ fontSize: 18, color: "error.main" }} />
          <Typography variant="caption" sx={{ fontWeight: 900, textTransform: "uppercase", letterSpacing: "1px" }}>{column.columnDef.header}</Typography>
        </Stack>
      ),
      Cell: ({ row, cell }) => {
        const isAdmin = cell.getValue() === "admin";
        const roleText = cell.getValue() || "User";
        const colorSet = isAdmin ? theme.palette.error : theme.palette.success;
        return (
          <Tooltip title={isAdmin ? "Revert to User" : "Elevate to Admin"}>
            <Chip
              icon={isAdmin ? <AdminIcon sx={{ fontSize: "14px !important", color: "#fff !important" }} /> : <UserIcon sx={{ fontSize: "14px !important", color: "#fff !important" }} />}
              label={roleText.toUpperCase()}
              size="small"
              onClick={() => handleRoleChange(row.original.id, isAdmin ? 'user' : 'admin')}
              sx={{
                fontWeight: 900,
                fontSize: "0.65rem",
                letterSpacing: "0.05em",
                bgcolor: colorSet.main,
                color: "#fff",
                border: "none",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "& .MuiChip-label": { px: 1.5, color: "#fff" },
                "& .MuiChip-icon": { color: "#fff" },
                "&:hover": {
                  bgcolor: colorSet.main,
                  opacity: 0.9,
                  transform: "translateY(-1px)",
                  boxShadow: `0 4px 12px ${alpha(colorSet.main, 0.4)}`
                }
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: "Registry Entry",
      Header: ({ column }) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <DateIcon sx={{ fontSize: 18, color: "warning.main" }} />
          <Typography variant="caption" sx={{ fontWeight: 900, textTransform: "uppercase", letterSpacing: "1px" }}>{column.columnDef.header}</Typography>
        </Stack>
      ),
      Cell: ({ cell }) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" fontWeight={800} color="text.secondary">
            {moment(cell.getValue()).format("MMM DD, YYYY")}
          </Typography>
        </Stack>
      ),
    },
  ], [theme.palette, handleRoleChange]);

  const partnerColumns = useMemo(() => [
    {
      accessorKey: "userName",
      header: "Partner Identity",
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar sx={{ width: 42, height: 42, bgcolor: alpha(theme.palette.secondary.main, 0.1), color: "secondary.main", fontWeight: 900 }}>
            {row.original.userName?.[0]}
          </Avatar>
          <Box>
            <Typography variant="body1" fontWeight={850}>{row.original.userName}</Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>{row.original.userEmail}</Typography>
          </Box>
        </Box>
      )
    },
    {
      accessorKey: "businessDetails.theaterDisplayName",
      header: "Business Hub",
      Cell: ({ row }) => (
        <Box>
          <Typography variant="body2" fontWeight={750}>{row.original.businessDetails?.theaterDisplayName || "N/A"}</Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {row.original.businessDetails?.address?.city}, {row.original.businessDetails?.address?.state}
          </Typography>
        </Box>
      )
    },
    {
      accessorKey: "currentPlan",
      header: "Subscription Plan",
      Cell: ({ cell }) => (
        <Chip 
          label={cell.getValue()?.toUpperCase() || "STARTER"} 
          size="small" 
          sx={{ fontWeight: 900, fontSize: "0.65rem", bgcolor: alpha(theme.palette.warning.main, 0.1), color: "warning.main", border: "none" }} 
        />
      )
    },
    {
      accessorKey: "kycStatus",
      header: "System Protocol",
      Cell: ({ cell }) => {
        const val = cell.getValue();
        const color = val === 'APPROVED' ? "success" : val === 'SUSPENDED' ? "error" : "warning";
        return (
          <Chip 
            icon={<SecurityIcon style={{ fontSize: 14, color: 'inherit' }} />}
            label={val?.replace('_', ' ') || "PENDING"} 
            size="small" 
            color={color}
            variant="outlined"
            sx={{ fontWeight: 900, fontSize: "0.65rem" }} 
          />
        );
      }
    },
    {
      id: "actions",
      header: "Operations",
      Cell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Intelligence View"><IconButton size="small" color="primary"><ViewIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
          {row.original.kycStatus === 'SUSPENDED' ? (
            <Tooltip title="Reactivate Protocol"><IconButton size="small" color="success"><VerifiedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
          ) : (
            <Tooltip title="Suspend Access"><IconButton size="small" color="error"><BlockIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
          )}
        </Stack>
      )
    }
  ], [theme]);

  const userTable = useMaterialReactTable({
    columns,
    data: users,
    muiTablePaperProps: { elevation: 0, sx: { borderRadius: "24px", border: `1px solid ${theme.palette.divider}`, overflow: "hidden", bgcolor: theme.palette.background.paper } },
    muiTableContainerProps: { sx: { maxWidth: '100%', overflowX: 'auto' } },
    muiTableHeadCellProps: { sx: { bgcolor: alpha(theme.palette.background.default, 0.5), py: 2.5, fontWeight: 900, fontSize: "0.7rem", color: theme.palette.text.secondary, letterSpacing: "0.1rem" } }
  });

  const partnerTable = useMaterialReactTable({
    columns: partnerColumns,
    data: allPartners,
    muiTablePaperProps: { elevation: 0, sx: { borderRadius: "24px", border: `1px solid ${theme.palette.divider}`, overflow: "hidden", bgcolor: theme.palette.background.paper } },
    muiTableHeadCellProps: { sx: { bgcolor: alpha(theme.palette.secondary.main, 0.05), py: 2.5, fontWeight: 900, fontSize: "0.7rem", color: theme.palette.secondary.main, letterSpacing: "0.1rem" } }
  });

  if (loading && users.length === 0) return <ModernLoader text="Deciphering Identity Database" />;

  return (
    <Box sx={{ width: "100%", pb: 4 }}>
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
          {/* Core Page Header */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            mb={{ xs: 3, md: 5 }}
            gap={{ xs: 2.5, md: 3 }}
          >
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 950, letterSpacing: "-0.04em", display: "flex", alignItems: "center", gap: 2.5, fontSize: { xs: "1.75rem", sm: "2.25rem", md: "3rem" } }}>
                <Box sx={{ p: 1, borderRadius: "12px", bgcolor: alpha(theme.palette.secondary.main, 0.1), color: theme.palette.secondary.main, display: "flex", flexShrink: 0 }}>
                  <PeopleIcon sx={{ fontSize: { xs: 22, md: 28 } }} />
                </Box>
              {activeTab === 0 ? "Identity Directory" : "Partner Ecosystem"}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 800, mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
              {activeTab === 0 ? (
                <>
                  <AdminIcon sx={{ fontSize: 16, color: "error.main" }} /> GLOBAL PERSONA REGISTRY & ACCESS PROTOCOLS
                </>
              ) : (
                <>
                  <BusinessIcon sx={{ fontSize: 16, color: "secondary.main" }} /> CINEMA NETWORK & SUBSCRIPTION INTELLIGENCE
                </>
              )}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2, width: isMobile ? "100%" : "auto" }}>
            <Paper elevation={0} sx={{ p: 0.5, borderRadius: "16px", bgcolor: alpha(theme.palette.divider, 0.05), border: `1px solid ${theme.palette.divider}` }}>
              <Tabs 
                value={activeTab} 
                onChange={(_, v) => setActiveTab(v)}
                sx={{ minHeight: 48, "& .MuiTabs-indicator": { height: "100%", borderRadius: "12px", zIndex: 0, opacity: 0.1 }, "& .MuiTab-root": { minHeight: 48, fontWeight: 900, zIndex: 1, textTransform: "none", fontSize: "0.85rem", px: 4, borderRadius: "12px" } }}
              >
                <Tab label="Users" />
                <Tab label="Partners" />
              </Tabs>
            </Paper>
            <Button
              variant="contained"
              startIcon={<SyncIcon className={syncing ? "fa-spin" : ""} />}
              onClick={handleSync}
              disabled={syncing}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: "16px",
                fontWeight: 900,
                background: theme.palette.primary.main,
                boxShadow: `0 10px 25px -5px ${alpha(theme.palette.primary.main, 0.3)}`,
                textTransform: "none",
                "&:hover": { background: alpha(theme.palette.primary.main, 0.9), transform: "translateY(-2px)" },
                transition: "all 0.2s"
              }}
            >
              Sync Matrix
            </Button>
          </Box>
        </Stack>

          {/* New Premium Filter Bar */}
          <Paper elevation={0} sx={{ p: 2, mb: 4, borderRadius: "24px", bgcolor: alpha(theme.palette.background.default, 0.5), border: `1px solid ${theme.palette.divider}`, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <Box sx={{ position: "relative", flexGrow: 1, maxWidth: "500px" }}>
              <SyncIcon sx={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: "text.secondary", opacity: 0.5, fontSize: 20 }} />
              <TextField 
                fullWidth 
                placeholder={activeTab === 0 ? "Search by name, email, or identity UID..." : "Search by partner, theater name, or email..."} 
                variant="standard" 
                InputProps={{ disableUnderline: true, sx: { pl: 6, pr: 2, height: 50, fontWeight: 700, fontSize: "14px" } }} 
              />
            </Box>
            <Divider orientation="vertical" flexItem sx={{ my: 1, opacity: 0.5 }} />
            <Stack direction="row" spacing={1}>
              <Chip label="ALL RECORDS" sx={{ fontWeight: 950, height: 36, bgcolor: activeTab === 0 ? "primary.main" : "secondary.main", color: "#fff" }} />
              <Chip label={activeTab === 0 ? "ADMINS" : "APPROVED"} variant="outlined" sx={{ fontWeight: 900, height: 36 }} />
              <Chip label={activeTab === 0 ? "CUSTOMERS" : "PENDING"} variant="outlined" sx={{ fontWeight: 900, height: 36 }} />
            </Stack>
          </Paper>

          <Grid container spacing={4} mb={6}>
            {[
              { label: "Global Personas", val: users.length, icon: <PeopleIcon sx={{ fontSize: 28 }} />, color: theme.palette.primary.main, trend: "+3.2%" },
              { label: "Privileged Admins", val: users.filter(u => u.role === 'admin').length, icon: <AdminIcon sx={{ fontSize: 28 }} />, color: theme.palette.error.main, trend: "SECURE" },
              { label: "Verified Users", val: users.filter(u => u.role === 'user').length, icon: <UserIcon sx={{ fontSize: 28 }} />, color: theme.palette.success.main, trend: "ACTIVE" }
            ].map((s, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                <Paper elevation={0} sx={{
                  p: 4, borderRadius: "32px", border: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.background.paper,
                  transition: "0.3s all", "&:hover": { transform: "translateY(-5px)", boxShadow: `0 20px 40px ${alpha(theme.palette.text.primary, 0.05)}` }
                }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
                    <Box sx={{ width: 54, height: 54, borderRadius: "16px", bgcolor: alpha(s.color, 0.1), color: s.color, display: "flex", alignItems: "center", justifyContent: "center" }}>{s.icon}</Box>
                    <Chip label={s.trend} size="small" sx={{ fontWeight: 950, fontSize: "10px", bgcolor: alpha(s.color, 0.1), color: s.color }} />
                  </Stack>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 950, color: theme.palette.text.primary, letterSpacing: "-0.04em", mb: 0.5 }}>{s.val}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "text.secondary", fontSize: "11px" }}>{s.label}</Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {activeTab === 0 ? (
            <MaterialReactTable table={userTable} />
          ) : (
            <MaterialReactTable table={partnerTable} />
          )}
        </motion.div>
    </Box>
  );
};

export default AdminUsers;
